import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  ArrowLeft,
  Save,
  X,
  Plus,
  Calendar,
  Clock,
  User,
  Target,
  AlertTriangle,
  FileText,
  Settings,
  Tag,
  Link,
  Paperclip,
  MessageSquare,
  CheckCircle,
  Building,
  Phone,
  Mail,
  MapPin,
  DollarSign,
  Users,
  Briefcase,
  Shield,
  BarChart3,
  Upload,
  Star,
  Award,
  UserCheck,
  FileCheck,
  CreditCard,
  Globe,
  Lock,
  Eye,
  Edit,
  Trash2,
  TrendingUp,
  TrendingDown,
  PieChart,
  Activity,
  Zap,
  Award as AwardIcon,
  ThumbsUp,
  ThumbsDown,
  AlertCircle,
  CheckCircle2,
  XCircle,
  Database
} from 'lucide-react';
import { useToast, ToastContainer } from '../common/Toast';
import PageBreadcrumb from '../common/PageBreadcrumb';
import PageHeader from '../common/PageHeader';
import VendorService from '../../services/vendorService';
import PerformanceReviewService from '../../services/performanceReviewService';
import API_CONFIG from '../../config/api';

// Helper function to map performance review data from backend to form data
const mapPerformanceReviewToFormData = (review) => {
  if (!review) return null;

  return {
    reviewPeriod: review.reviewPeriod || '',
    startDate: review.startDate ? new Date(review.startDate).toISOString().split('T')[0] : '',
    endDate: review.endDate ? new Date(review.endDate).toISOString().split('T')[0] : '',
    reviewType: review.reviewType || 'quarterly',
    reviewStatus: review.reviewStatus || 'draft',
    priority: review.priority || 'medium',
    
    subcontractor: {
      id: review.subcontractor?._id || review.subcontractor || '',
      name: review.subcontractor?.name || '',
      company: review.subcontractor?.name || '',
      category: review.subcontractor?.industry || review.subcontractor?.sector || '',
      contactPerson: review.subcontractor?.primaryContact?.name || '',
      email: review.subcontractor?.primaryContact?.email || review.subcontractor?.communication?.email || '',
      phone: review.subcontractor?.primaryContact?.phone || review.subcontractor?.communication?.phone || ''
    },
    
    project: {
      id: review.project?._id || review.project || '',
      name: review.project?.name || '',
      description: review.project?.description || '',
      startDate: review.project?.startDate ? new Date(review.project.startDate).toISOString().split('T')[0] : '',
      endDate: review.project?.endDate ? new Date(review.project.endDate).toISOString().split('T')[0] : '',
      budget: review.project?.budget ? String(review.project.budget) : '',
      status: review.project?.status || ''
    },
    
    qualityScore: review.qualityScore || 0,
    timelinessScore: review.timelinessScore || 0,
    safetyScore: review.safetyScore || 0,
    communicationScore: review.communicationScore || 0,
    overallScore: review.overallScore || 0,
    
    performanceDetails: {
      strengths: review.performanceDetails?.strengths || [],
      weaknesses: review.performanceDetails?.weaknesses || [],
      achievements: review.performanceDetails?.achievements || [],
      improvements: review.performanceDetails?.improvements || [],
      challenges: review.performanceDetails?.challenges || []
    },
    
    qualityMetrics: {
      defectRate: review.qualityMetrics?.defectRate || 0,
      reworkRate: review.qualityMetrics?.reworkRate || 0,
      customerSatisfaction: review.qualityMetrics?.customerSatisfaction || 0,
      complianceScore: review.qualityMetrics?.complianceScore || 0,
      documentationQuality: review.qualityMetrics?.documentationQuality || 0
    },
    
    timelinessMetrics: {
      onTimeDelivery: review.timelinessMetrics?.onTimeDelivery || 0,
      scheduleAdherence: review.timelinessMetrics?.scheduleAdherence || 0,
      responseTime: review.timelinessMetrics?.responseTime || 0,
      deadlineCompliance: review.timelinessMetrics?.deadlineCompliance || 0,
      projectDuration: review.timelinessMetrics?.projectDuration || 0
    },
    
    safetyMetrics: {
      safetyIncidents: review.safetyMetrics?.safetyIncidents || 0,
      safetyTraining: review.safetyMetrics?.safetyTraining || 0,
      complianceViolations: review.safetyMetrics?.complianceViolations || 0,
      safetyScore: review.safetyMetrics?.safetyScore || 0,
      riskManagement: review.safetyMetrics?.riskManagement || 0
    },
    
    communicationMetrics: {
      responsiveness: review.communicationMetrics?.responsiveness || 0,
      clarity: review.communicationMetrics?.clarity || 0,
      frequency: review.communicationMetrics?.frequency || 0,
      problemResolution: review.communicationMetrics?.problemResolution || 0,
      collaboration: review.communicationMetrics?.collaboration || 0
    },
    
    financialMetrics: {
      budgetAdherence: review.financialMetrics?.budgetAdherence || 0,
      costEfficiency: review.financialMetrics?.costEfficiency || 0,
      valueDelivered: review.financialMetrics?.valueDelivered || 0,
      paymentTimeliness: review.financialMetrics?.paymentTimeliness || 0,
      costOverruns: review.financialMetrics?.costOverruns || 0
    },
    
    goals: {
      previousGoals: review.goals?.previousGoals || [],
      currentGoals: review.goals?.currentGoals || [],
      futureGoals: review.goals?.futureGoals || [],
      goalAchievement: review.goals?.goalAchievement || 0,
      goalClarity: review.goals?.goalClarity || 0
    },
    
    feedback: {
      managerComments: review.feedback?.managerComments || '',
      subcontractorComments: review.feedback?.subcontractorComments || '',
      clientFeedback: review.feedback?.clientFeedback || '',
      teamFeedback: review.feedback?.teamFeedback || '',
      recommendations: review.feedback?.recommendations || ''
    },
    
    actionItems: review.actionItems || [],
    improvementPlan: {
      areas: review.improvementPlan?.areas || [],
      timeline: review.improvementPlan?.timeline || '',
      resources: review.improvementPlan?.resources || [],
      milestones: review.improvementPlan?.milestones || []
    },
    
    documents: review.documents || [],
    attachments: review.attachments || [],
    evidence: review.evidence || [],
    
    reviewProcess: {
      reviewer: review.reviewProcess?.reviewer?._id || review.reviewProcess?.reviewer || '',
      reviewDate: review.reviewProcess?.reviewDate ? new Date(review.reviewProcess.reviewDate).toISOString().split('T')[0] : '',
      nextReviewDate: review.reviewProcess?.nextReviewDate ? new Date(review.reviewProcess.nextReviewDate).toISOString().split('T')[0] : '',
      approvalStatus: review.reviewProcess?.approvalStatus || 'pending',
      approvalNotes: review.reviewProcess?.approvalNotes || ''
    },
    
    ratings: {
      overallRating: review.ratings?.overallRating || 0,
      recommendation: review.ratings?.recommendation || '',
      contractRenewal: review.ratings?.contractRenewal || '',
      performanceTier: review.ratings?.performanceTier || '',
      bonusEligibility: review.ratings?.bonusEligibility || false
    },
    
    settings: {
      autoCalculate: review.settings?.autoCalculate !== undefined ? review.settings.autoCalculate : true,
      sendNotifications: review.settings?.sendNotifications !== undefined ? review.settings.sendNotifications : true,
      requireApproval: review.settings?.requireApproval !== undefined ? review.settings.requireApproval : true,
      publicVisibility: review.settings?.publicVisibility !== undefined ? review.settings.publicVisibility : false,
      includeClientFeedback: review.settings?.includeClientFeedback !== undefined ? review.settings.includeClientFeedback : true
    }
  };
};

const CreatePerformanceReview = ({ editMode = false, viewMode = false, reviewId = null }) => {
  const navigate = useNavigate();
  const { success, error: showError, toasts, removeToast } = useToast();
  
  // Tab management
  const [activeTab, setActiveTab] = useState('basic');
  
  // Test mode state
  const [testMode, setTestMode] = useState(false);
  const [loading, setLoading] = useState(editMode || viewMode);
  
  // Data loading states
  const [subcontractors, setSubcontractors] = useState([]);
  const [projects, setProjects] = useState([]);
  const [loadingData, setLoadingData] = useState(false);
  
  // Form state
  const [formData, setFormData] = useState({
    // Basic Information
    reviewPeriod: '',
    startDate: '',
    endDate: '',
    reviewType: 'quarterly',
    reviewStatus: 'draft',
    priority: 'medium',
    
    // Subcontractor Information
    subcontractor: {
      id: '',
      name: '',
      company: '',
      category: '',
      contactPerson: '',
      email: '',
      phone: ''
    },
    
    // Project Information
    project: {
      id: '',
      name: '',
      description: '',
      startDate: '',
      endDate: '',
      budget: '',
      status: ''
    },
    
    // Performance Metrics
    qualityScore: 0,
    timelinessScore: 0,
    safetyScore: 0,
    communicationScore: 0,
    overallScore: 0,
    
    // Detailed Performance
    performanceDetails: {
      strengths: [],
      weaknesses: [],
      achievements: [],
      improvements: [],
      challenges: []
    },
    
    // Quality Assessment
    qualityMetrics: {
      defectRate: 0,
      reworkRate: 0,
      customerSatisfaction: 0,
      complianceScore: 0,
      documentationQuality: 0
    },
    
    // Timeliness Assessment
    timelinessMetrics: {
      onTimeDelivery: 0,
      scheduleAdherence: 0,
      responseTime: 0,
      deadlineCompliance: 0,
      projectDuration: 0
    },
    
    // Safety Assessment
    safetyMetrics: {
      safetyIncidents: 0,
      safetyTraining: 0,
      complianceViolations: 0,
      safetyScore: 0,
      riskManagement: 0
    },
    
    // Communication Assessment
    communicationMetrics: {
      responsiveness: 0,
      clarity: 0,
      frequency: 0,
      problemResolution: 0,
      collaboration: 0
    },
    
    // Financial Performance
    financialMetrics: {
      budgetAdherence: 0,
      costEfficiency: 0,
      valueDelivered: 0,
      paymentTimeliness: 0,
      costOverruns: 0
    },
    
    // Goals and Objectives
    goals: {
      previousGoals: [],
      currentGoals: [],
      futureGoals: [],
      goalAchievement: 0,
      goalClarity: 0
    },
    
    // Feedback and Comments
    feedback: {
      managerComments: '',
      subcontractorComments: '',
      clientFeedback: '',
      teamFeedback: '',
      recommendations: ''
    },
    
    // Action Items
    actionItems: [],
    improvementPlan: {
      areas: [],
      timeline: '',
      resources: [],
      milestones: []
    },
    
    // Documentation
    documents: [],
    attachments: [],
    evidence: [],
    
    // Review Process
    reviewProcess: {
      reviewer: '',
      reviewDate: '',
      nextReviewDate: '',
      approvalStatus: 'pending',
      approvalNotes: ''
    },
    
    // Ratings and Scores
    ratings: {
      overallRating: 0,
      recommendation: '',
      contractRenewal: '',
      performanceTier: '',
      bonusEligibility: false
    },
    
    // Advanced Settings
    settings: {
      autoCalculate: true,
      sendNotifications: true,
      requireApproval: true,
      publicVisibility: false,
      includeClientFeedback: true
    }
  });

  const [isSubmitting, setIsSubmitting] = useState(false);

  // Helper function to get disabled/readOnly props for form inputs
  const getInputProps = () => ({
    disabled: viewMode,
    readOnly: viewMode
  });

  // Load performance review data if in edit or view mode
  useEffect(() => {
    const loadReviewData = async () => {
      if (reviewId) {
        setLoading(true);
        try {
          const response = await PerformanceReviewService.getPerformanceReview(reviewId);
          if (response && response.success) {
            const mappedData = mapPerformanceReviewToFormData(response.data);
            if (mappedData) {
              setFormData(mappedData);
            }
          } else {
            showError('Failed to load performance review details.');
            navigate('/subcontracting/performance');
          }
        } catch (err) {
          console.error('Error loading performance review:', err);
          const errorMessage = err?.message || err?.response?.data?.message || 'Failed to load performance review details.';
          showError(errorMessage);
          setTimeout(() => {
            navigate('/subcontracting/performance');
          }, 2000);
        } finally {
          setLoading(false);
        }
      }
    };
    
    loadReviewData();
  }, [reviewId]);

  const loadProjects = async () => {
      try {
    const response = await fetch(API_CONFIG.ENDPOINTS.PROJECTS, {
          headers: API_CONFIG.HEADERS
        });
        
    if (!response.ok) throw new Error('Failed to load projects');

          const result = await response.json();
    const list = result?.data?.projects || result?.projects || [];

    setProjects(list);
      } catch (err) {
    console.error('Error loading projects:', err);
    setProjects([]);
  }
};

const loadSubcontractors = async () => {
      try {
    const response = await VendorService.getVendors({
      limit: 100
        });
        
    if (response?.success) {
      setSubcontractors(response.data || []);
    } else {
      setSubcontractors([]);
        }
      } catch (err) {
    console.error('Error loading subcontractors:', err);
    setSubcontractors([]);
      }
    };
    
 useEffect(() => {
  loadProjects();
  loadSubcontractors();
  }, []);


  // Handle subcontractor selection - auto-fill contact info
  const handleSubcontractorChange = (vendorId) => {
  const selectedVendor = subcontractors.find(
    v => String(v._id || v.vendorId) === String(vendorId)
  );

    if (selectedVendor) {
      setFormData(prev => ({
        ...prev,
        subcontractor: {
        id: String(selectedVendor._id || selectedVendor.vendorId),
          name: selectedVendor.name || '',
          company: selectedVendor.name || '',
          category: selectedVendor.industry || selectedVendor.sector || '',
          contactPerson: selectedVendor.primaryContact?.name || '',
          email: selectedVendor.primaryContact?.email || selectedVendor.communication?.email || '',
          phone: selectedVendor.primaryContact?.phone || selectedVendor.communication?.phone || ''
        }
      }));
    } else {
      setFormData(prev => ({
        ...prev,
        subcontractor: {
          ...prev.subcontractor,
          id: vendorId
        }
      }));
    }
  };

  // Handle project selection - auto-fill project info
  const handleProjectChange = (projectId) => {
  const selectedProject = projects.find(
    p => String(p._id) === String(projectId)
  );

    if (selectedProject) {
      setFormData(prev => ({
        ...prev,
        project: {
        id: String(selectedProject._id),
          name: selectedProject.name || '',
          description: selectedProject.description || '',
        startDate: selectedProject.startDate
          ? new Date(selectedProject.startDate).toISOString().split('T')[0]
          : '',
        endDate: selectedProject.endDate
          ? new Date(selectedProject.endDate).toISOString().split('T')[0]
          : '',
          budget: selectedProject.budget ? String(selectedProject.budget) : '',
          status: selectedProject.status || ''
        }
      }));
    } else {
      setFormData(prev => ({
        ...prev,
        project: {
          ...prev.project,
          id: projectId
        }
      }));
    }
  };

  // Function to fill form with test data
  const fillTestData = () => {
    const testData = {
      reviewPeriod: 'Q1 2024',
      startDate: '2024-01-01',
      endDate: '2024-03-31',
      reviewType: 'quarterly',
      reviewStatus: 'draft',
      priority: 'high',
      
      subcontractor: {
        id: subcontractors.length > 0 ? (subcontractors[0]._id || subcontractors[0].vendorId) : '',
        name: subcontractors.length > 0 ? subcontractors[0].name : 'ABC Construction Ltd.',
        company: subcontractors.length > 0 ? subcontractors[0].name : 'ABC Construction Ltd.',
        category: subcontractors.length > 0 ? (subcontractors[0].industry || subcontractors[0].sector) : 'Construction',
        contactPerson: subcontractors.length > 0 ? (subcontractors[0].primaryContact?.name || 'John Smith') : 'John Smith',
        email: subcontractors.length > 0 ? (subcontractors[0].primaryContact?.email || 'john.smith@abcconstruction.com') : 'john.smith@abcconstruction.com',
        phone: subcontractors.length > 0 ? (subcontractors[0].primaryContact?.phone || '555-123-4567') : '555-123-4567'
      },
      
      project: {
        id: projects.length > 0 ? projects[0]._id : '',
        name: projects.length > 0 ? projects[0].name : 'Office Building Construction',
        description: projects.length > 0 ? projects[0].description : 'Construction of new office building',
        startDate: projects.length > 0 && projects[0].startDate ? new Date(projects[0].startDate).toISOString().split('T')[0] : '2024-01-15',
        endDate: projects.length > 0 && projects[0].endDate ? new Date(projects[0].endDate).toISOString().split('T')[0] : '2024-06-30',
        budget: projects.length > 0 && projects[0].budget ? String(projects[0].budget) : '500000',
        status: projects.length > 0 ? projects[0].status : 'in-progress'
      },
      
      qualityScore: 8,
      timelinessScore: 7,
      safetyScore: 9,
      communicationScore: 8,
      overallScore: 8,
      
      performanceDetails: {
        strengths: ['Excellent quality work', 'Strong communication', 'Reliable team'],
        weaknesses: ['Occasional delays', 'Documentation needs improvement'],
        achievements: ['Completed project ahead of schedule', 'Zero safety incidents'],
        improvements: ['Improve documentation quality', 'Enhance project planning'],
        challenges: ['Weather delays', 'Material supply issues']
      },
      
      qualityMetrics: {
        defectRate: 2,
        reworkRate: 5,
        customerSatisfaction: 90,
        complianceScore: 95,
        documentationQuality: 75
      },
      
      timelinessMetrics: {
        onTimeDelivery: 85,
        scheduleAdherence: 80,
        responseTime: 90,
        deadlineCompliance: 85,
        projectDuration: 180
      },
      
      safetyMetrics: {
        safetyIncidents: 0,
        safetyTraining: 100,
        complianceViolations: 0,
        safetyScore: 98,
        riskManagement: 95
      },
      
      communicationMetrics: {
        responsiveness: 90,
        clarity: 85,
        frequency: 80,
        problemResolution: 88,
        collaboration: 90
      },
      
      financialMetrics: {
        budgetAdherence: 95,
        costEfficiency: 88,
        valueDelivered: 92,
        paymentTimeliness: 100,
        costOverruns: 0
      },
      
      goals: {
        previousGoals: ['Complete project on time', 'Maintain quality standards'],
        currentGoals: ['Improve documentation', 'Enhance communication'],
        futureGoals: ['Expand capabilities', 'Increase efficiency'],
        goalAchievement: 85,
        goalClarity: 9
      },
      
      feedback: {
        managerComments: 'Overall excellent performance. The team demonstrated strong commitment and quality work throughout the review period.',
        subcontractorComments: 'We appreciate the feedback and look forward to continued collaboration.',
        clientFeedback: 'Very satisfied with the work quality and professionalism.',
        teamFeedback: 'Great working relationship and effective communication.',
        recommendations: 'Continue current performance levels and focus on documentation improvements.'
      },
      
      actionItems: ['Improve documentation process', 'Enhance project planning', 'Increase communication frequency'],
      
      improvementPlan: {
        areas: ['Documentation', 'Planning', 'Communication'],
        timeline: '3 months',
        resources: ['Training materials', 'Additional staff', 'Software tools'],
        milestones: ['Complete training', 'Implement new process', 'Review progress']
      },
      
      reviewProcess: {
        reviewer: '',
        reviewDate: new Date().toISOString().split('T')[0],
        nextReviewDate: new Date(Date.now() + 90 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
        approvalStatus: 'pending',
        approvalNotes: ''
      },
      
      ratings: {
        overallRating: 8,
        recommendation: 'Continue contract',
        contractRenewal: 'Recommended',
        performanceTier: 'Excellent',
        bonusEligibility: true
      }
    };
    
    setFormData(prev => ({ ...prev, ...testData }));
    success('Test data filled successfully!');
  };

  // Tab configuration
  const tabs = [
    {
      id: 'basic',
      label: 'Review Details',
      icon: <FileText size={18} />
    },
    {
      id: 'metrics',
      label: 'Performance Metrics',
      icon: <BarChart3 size={18} />
    },
    {
      id: 'assessment',
      label: 'Detailed Assessment',
      icon: <Target size={18} />
    },
    {
      id: 'feedback',
      label: 'Feedback & Actions',
      icon: <MessageSquare size={18} />
    }
  ];

  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleNestedInputChange = (parentField, childField, value) => {
    setFormData(prev => ({
      ...prev,
      [parentField]: {
        ...prev[parentField],
        [childField]: value
      }
    }));
  };

  const handleArrayAdd = (field, value) => {
    if (value && value.trim()) {
      // Handle nested paths like 'performanceDetails.strengths'
      if (field.includes('.')) {
        const [parent, child] = field.split('.');
        setFormData(prev => ({
          ...prev,
          [parent]: {
            ...prev[parent],
            [child]: [...(prev[parent]?.[child] || []), value.trim()]
          }
        }));
      } else {
        setFormData(prev => ({
          ...prev,
          [field]: [...(prev[field] || []), value.trim()]
        }));
      }
    }
  };

  const handleArrayRemove = (field, index) => {
    // Handle nested paths like 'performanceDetails.strengths'
    if (field.includes('.')) {
      const [parent, child] = field.split('.');
      setFormData(prev => ({
        ...prev,
        [parent]: {
          ...prev[parent],
          [child]: (prev[parent]?.[child] || []).filter((_, i) => i !== index)
        }
      }));
    } else {
      setFormData(prev => ({
        ...prev,
        [field]: (prev[field] || []).filter((_, i) => i !== index)
      }));
    }
  };

  const handleScoreChange = (field, value) => {
    const score = Math.max(0, Math.min(10, parseInt(value) || 0));
    
    setFormData(prev => {
      const updatedData = {
        ...prev,
        [field]: score
      };
      
      // Auto-calculate overall score
      if (field === 'qualityScore' || field === 'timelinessScore' || field === 'safetyScore' || field === 'communicationScore') {
        const quality = field === 'qualityScore' ? score : updatedData.qualityScore;
        const timeliness = field === 'timelinessScore' ? score : updatedData.timelinessScore;
        const safety = field === 'safetyScore' ? score : updatedData.safetyScore;
        const communication = field === 'communicationScore' ? score : updatedData.communicationScore;
        
        const overall = Math.round((quality + timeliness + safety + communication) / 4);
        updatedData.overallScore = overall;
      }
      
      return updatedData;
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    
    try {
      // Format data for backend
      const reviewData = {
        reviewPeriod: formData.reviewPeriod,
        startDate: formData.startDate,
        endDate: formData.endDate,
        reviewType: formData.reviewType,
        reviewStatus: formData.reviewStatus || 'draft',
        priority: formData.priority,
        subcontractor: formData.subcontractor.id,
        project: formData.project.id || undefined,
        qualityScore: formData.qualityScore,
        timelinessScore: formData.timelinessScore,
        safetyScore: formData.safetyScore,
        communicationScore: formData.communicationScore,
        overallScore: formData.overallScore,
        performanceDetails: formData.performanceDetails,
        qualityMetrics: formData.qualityMetrics,
        timelinessMetrics: formData.timelinessMetrics,
        safetyMetrics: formData.safetyMetrics,
        communicationMetrics: formData.communicationMetrics,
        financialMetrics: formData.financialMetrics,
        goals: formData.goals,
        feedback: formData.feedback,
        actionItems: formData.actionItems,
        improvementPlan: formData.improvementPlan,
        reviewProcess: {
          ...formData.reviewProcess,
          reviewer: formData.reviewProcess.reviewer && formData.reviewProcess.reviewer.trim() !== '' 
            ? formData.reviewProcess.reviewer 
            : undefined
        },
        ratings: formData.ratings,
        settings: formData.settings
      };

      // Helper function to clean nested objects and remove undefined/empty values
      const cleanData = (obj) => {
        const cleaned = {};
        Object.keys(obj).forEach(key => {
          const value = obj[key];
          if (value === undefined || value === null || value === '') {
            return; // Skip undefined, null, or empty string values
          }
          if (Array.isArray(value)) {
            if (value.length > 0) {
              cleaned[key] = value;
            }
          } else if (typeof value === 'object' && !(value instanceof Date)) {
            const cleanedNested = cleanData(value);
            if (Object.keys(cleanedNested).length > 0) {
              cleaned[key] = cleanedNested;
            }
          } else {
            cleaned[key] = value;
          }
        });
        return cleaned;
      };

      const cleanedReviewData = cleanData(reviewData);

      let response;
      if (editMode && reviewId) {
        response = await PerformanceReviewService.updatePerformanceReview(reviewId, cleanedReviewData);
        if (response && response.success) {
          success('Performance review updated successfully!');
          setTimeout(() => {
            navigate('/subcontracting/performance');
          }, 1000);
        } else {
          showError(response?.message || 'Failed to update performance review. Please try again.');
        }
      } else {
        response = await PerformanceReviewService.createPerformanceReview(cleanedReviewData);
        if (response && response.success) {
          success('Performance review created successfully!');
          setTimeout(() => {
            navigate('/subcontracting/performance');
          }, 1000);
        } else {
          showError(response?.message || 'Failed to create performance review. Please try again.');
        }
      }
    } catch (err) {
      console.error('Error creating performance review:', err);
      const errorMessage = err?.message || err?.response?.data?.message || 'Failed to create performance review. Please try again.';
      showError(errorMessage);
    } finally {
      setIsSubmitting(false);
    }
  };

  const renderTabContent = () => {
    switch (activeTab) {
      case 'basic':
        return renderReviewDetails();
      case 'metrics':
        return renderPerformanceMetrics();
      case 'assessment':
        return renderDetailedAssessment();
      case 'feedback':
        return renderFeedbackActions();
      default:
        return renderReviewDetails();
    }
  };

  const renderReviewDetails = () => (
    <div className="project-form-section">
      <div className="section-header">
        <h3 className="section-title">Performance Review Details</h3>
        <p className="section-description">Basic information and review period details</p>
      </div>
      
      <div className="form-grid-4">
        <div className="form-group">
          <label className="form-label">Review Period *</label>
            <input
              type="text"
              className={`form-control ${viewMode ? 'view-mode' : ''}`}
              value={formData.reviewPeriod}
              onChange={(e) => handleInputChange('reviewPeriod', e.target.value)}
              placeholder="e.g., Q1 2024, January 2024"
              required
              {...getInputProps()}
            />
        </div>
        
        <div className="form-group">
          <label className="form-label">Start Date *</label>
          <input
            type="date"
            className="form-control"
            value={formData.startDate}
            onChange={(e) => handleInputChange('startDate', e.target.value)}
            required
          />
        </div>
        
        <div className="form-group">
          <label className="form-label">End Date *</label>
          <input
            type="date"
            className="form-control"
            value={formData.endDate}
            onChange={(e) => handleInputChange('endDate', e.target.value)}
            required
          />
        </div>
        
        <div className="form-group">
          <label className="form-label">Review Type</label>
          <select
            className="form-select"
            value={formData.reviewType}
            onChange={(e) => handleInputChange('reviewType', e.target.value)}
          >
            <option value="quarterly">Quarterly</option>
            <option value="monthly">Monthly</option>
            <option value="annual">Annual</option>
            <option value="project-based">Project-based</option>
            <option value="ad-hoc">Ad-hoc</option>
          </select>
        </div>
        
        <div className="form-group">
          <label className="form-label">Priority</label>
          <select
            className="form-select"
            value={formData.priority}
            onChange={(e) => handleInputChange('priority', e.target.value)}
          >
            <option value="low">Low</option>
            <option value="medium">Medium</option>
            <option value="high">High</option>
            <option value="urgent">Urgent</option>
          </select>
        </div>
      </div>
      
      <div className="form-group">
        <h4 className="section-subtitle">Subcontractor Information</h4>
        <div className="form-grid-4">
          <div className="form-group">
            <label className="form-label">Subcontractor *</label>
            <select
              className="form-select"
              value={formData.subcontractor.id}
              onChange={(e) => handleSubcontractorChange(e.target.value)}
              required
              disabled={loadingData}
            >
              <option value="">Select subcontractor</option>
              {subcontractors.map((vendor) => (
                <option
  key={vendor._id || vendor.vendorId}
  value={String(vendor._id || vendor.vendorId)}
>
                  {vendor.name} {vendor.vendorId ? `(${vendor.vendorId})` : ''}
                </option>
              ))}
            </select>
          </div>
          
          <div className="form-group">
            <label className="form-label">Contact Person</label>
            <input
              type="text"
              className="form-control"
              value={formData.subcontractor.contactPerson}
              onChange={(e) => handleNestedInputChange('subcontractor', 'contactPerson', e.target.value)}
              placeholder="Enter contact person name"
            />
          </div>
          
          <div className="form-group">
            <label className="form-label">Email</label>
            <input
              type="email"
              className="form-control"
              value={formData.subcontractor.email}
              onChange={(e) => handleNestedInputChange('subcontractor', 'email', e.target.value)}
              placeholder="Enter email address"
            />
          </div>
          
          <div className="form-group">
            <label className="form-label">Phone</label>
            <input
              type="tel"
              className="form-control"
              value={formData.subcontractor.phone}
              onChange={(e) => handleNestedInputChange('subcontractor', 'phone', e.target.value)}
              placeholder="Enter phone number"
            />
          </div>
        </div>
      </div>
      
      <div className="form-group">
        <h4 className="section-subtitle">Project Information</h4>
        <div className="form-grid-4">
          <div className="form-group">
            <label className="form-label">Project</label>
            <select
              className="form-select"
              value={formData.project.id}
              onChange={(e) => handleProjectChange(e.target.value)}
              disabled={loadingData}
            >
              <option value="">Select project</option>
              {projects.map((project) => (
                <option key={project._id} value={String(project._id)}>
                  {project.name} {project.projectCode ? `(${project.projectCode})` : ''}
                </option>
              ))}
            </select>
          </div>
          
          <div className="form-group">
            <label className="form-label">Project Budget</label>
            <input
              type="number"
              className="form-control"
              value={formData.project.budget}
              onChange={(e) => handleNestedInputChange('project', 'budget', e.target.value)}
              placeholder="Enter project budget"
            />
          </div>
          
          <div className="form-group">
            <label className="form-label">Project Status</label>
            <select
              className="form-select"
              value={formData.project.status}
              onChange={(e) => handleNestedInputChange('project', 'status', e.target.value)}
            >
              <option value="">Select status</option>
              <option value="planning">Planning</option>
              <option value="in-progress">In Progress</option>
              <option value="completed">Completed</option>
              <option value="on-hold">On Hold</option>
              <option value="cancelled">Cancelled</option>
            </select>
          </div>
        </div>
      </div>
    </div>
  );

  const renderPerformanceMetrics = () => (
    <div className="project-form-section">
      <div className="section-header">
        <h3 className="section-title">Performance Metrics</h3>
        <p className="section-description">Rate performance across key areas (1-10 scale)</p>
      </div>
      
      <div className="form-group">
        <h4 className="section-subtitle">Overall Performance Scores</h4>
        <div className="form-grid-4">
          <div className="form-group">
            <label className="form-label">Quality Score: {formData.qualityScore}/10</label>
            <input
              type="range"
              className="form-range"
              min="0"
              max="10"
              value={formData.qualityScore}
              onChange={(e) => handleScoreChange('qualityScore', e.target.value)}
            />
          </div>
          
          <div className="form-group">
            <label className="form-label">Timeliness Score: {formData.timelinessScore}/10</label>
            <input
              type="range"
              className="form-range"
              min="0"
              max="10"
              value={formData.timelinessScore}
              onChange={(e) => handleScoreChange('timelinessScore', e.target.value)}
            />
          </div>
          
          <div className="form-group">
            <label className="form-label">Safety Score: {formData.safetyScore}/10</label>
            <input
              type="range"
              className="form-range"
              min="0"
              max="10"
              value={formData.safetyScore}
              onChange={(e) => handleScoreChange('safetyScore', e.target.value)}
            />
          </div>
          
          <div className="form-group">
            <label className="form-label">Communication Score: {formData.communicationScore}/10</label>
            <input
              type="range"
              className="form-range"
              min="0"
              max="10"
              value={formData.communicationScore}
              onChange={(e) => handleScoreChange('communicationScore', e.target.value)}
            />
          </div>
        </div>
        
        <div className="form-group">
          <div className="score-display">
            <h4>Overall Score: {formData.overallScore}/10</h4>
            <div className="score-bar">
              <div 
                className="score-fill" 
                style={{ width: `${(formData.overallScore / 10) * 100}%` }}
              ></div>
            </div>
          </div>
        </div>
      </div>
      
      <div className="form-group">
        <h4 className="section-subtitle">Quality Metrics</h4>
        <div className="form-grid-5">
          <div className="form-group">
            <label className="form-label">Defect Rate (%)</label>
            <input
              type="number"
              className="form-control"
              value={formData.qualityMetrics.defectRate}
              onChange={(e) => handleNestedInputChange('qualityMetrics', 'defectRate', e.target.value)}
              min="0"
              max="100"
            />
          </div>
          
          <div className="form-group">
            <label className="form-label">Rework Rate (%)</label>
            <input
              type="number"
              className="form-control"
              value={formData.qualityMetrics.reworkRate}
              onChange={(e) => handleNestedInputChange('qualityMetrics', 'reworkRate', e.target.value)}
              min="0"
              max="100"
            />
          </div>
          
          <div className="form-group">
            <label className="form-label">Customer Satisfaction (%)</label>
            <input
              type="number"
              className="form-control"
              value={formData.qualityMetrics.customerSatisfaction}
              onChange={(e) => handleNestedInputChange('qualityMetrics', 'customerSatisfaction', e.target.value)}
              min="0"
              max="100"
            />
          </div>
          
          <div className="form-group">
            <label className="form-label">Compliance Score (%)</label>
            <input
              type="number"
              className="form-control"
              value={formData.qualityMetrics.complianceScore}
              onChange={(e) => handleNestedInputChange('qualityMetrics', 'complianceScore', e.target.value)}
              min="0"
              max="100"
            />
          </div>
          
          <div className="form-group">
            <label className="form-label">Documentation Quality (%)</label>
            <input
              type="number"
              className="form-control"
              value={formData.qualityMetrics.documentationQuality}
              onChange={(e) => handleNestedInputChange('qualityMetrics', 'documentationQuality', e.target.value)}
              min="0"
              max="100"
            />
          </div>
        </div>
      </div>
      
      <div className="form-group">
        <h4 className="section-subtitle">Safety Metrics</h4>
        <div className="form-grid-5">
          <div className="form-group">
            <label className="form-label">Safety Incidents</label>
            <input
              type="number"
              className="form-control"
              value={formData.safetyMetrics.safetyIncidents}
              onChange={(e) => handleNestedInputChange('safetyMetrics', 'safetyIncidents', e.target.value)}
              min="0"
            />
          </div>
          
          <div className="form-group">
            <label className="form-label">Safety Training (%)</label>
            <input
              type="number"
              className="form-control"
              value={formData.safetyMetrics.safetyTraining}
              onChange={(e) => handleNestedInputChange('safetyMetrics', 'safetyTraining', e.target.value)}
              min="0"
              max="100"
            />
          </div>
          
          <div className="form-group">
            <label className="form-label">Compliance Violations</label>
            <input
              type="number"
              className="form-control"
              value={formData.safetyMetrics.complianceViolations}
              onChange={(e) => handleNestedInputChange('safetyMetrics', 'complianceViolations', e.target.value)}
              min="0"
            />
          </div>
          
          <div className="form-group">
            <label className="form-label">Safety Score (%)</label>
            <input
              type="number"
              className="form-control"
              value={formData.safetyMetrics.safetyScore}
              onChange={(e) => handleNestedInputChange('safetyMetrics', 'safetyScore', e.target.value)}
              min="0"
              max="100"
            />
          </div>
          
          <div className="form-group">
            <label className="form-label">Risk Management (%)</label>
            <input
              type="number"
              className="form-control"
              value={formData.safetyMetrics.riskManagement}
              onChange={(e) => handleNestedInputChange('safetyMetrics', 'riskManagement', e.target.value)}
              min="0"
              max="100"
            />
          </div>
        </div>
      </div>
    </div>
  );

  const renderDetailedAssessment = () => (
    <div className="project-form-section">
      <div className="section-header">
        <h3 className="section-title">Detailed Performance Assessment</h3>
        <p className="section-description">Comprehensive evaluation of strengths, weaknesses, and areas for improvement</p>
      </div>
      
      <div className="form-group">
        <label className="form-label">Strengths</label>
        <div className="array-input-group">
          {formData.performanceDetails.strengths.map((strength, index) => (
            <div key={index} className="array-input-item">
              <input
                type="text"
                className="form-control"
                value={strength}
                onChange={(e) => {
                  const newStrengths = [...formData.performanceDetails.strengths];
                  newStrengths[index] = e.target.value;
                  setFormData(prev => ({
                    ...prev,
                    performanceDetails: { ...prev.performanceDetails, strengths: newStrengths }
                  }));
                }}
                placeholder="Enter strength"
              />
              <button
                type="button"
                className="btn btn-outline-danger btn-sm"
                onClick={() => handleArrayRemove('performanceDetails.strengths', index)}
              >
                <X size={16} />
              </button>
            </div>
          ))}
          <div className="array-input-add">
            <input
              type="text"
              className="form-control"
              placeholder="Add new strength"
              onKeyPress={(e) => {
                if (e.key === 'Enter') {
                  handleArrayAdd('performanceDetails.strengths', e.target.value);
                  e.target.value = '';
                }
              }}
            />
            <button
              type="button"
              className="btn btn-outline-primary"
              onClick={(e) => {
                const input = e.target.previousElementSibling;
                if (input && input.value) {
                  handleArrayAdd('performanceDetails.strengths', input.value);
                  input.value = '';
                }
              }}
            >
              <Plus size={16} />
            </button>
          </div>
        </div>
      </div>
      
      <div className="form-group">
        <label className="form-label">Areas for Improvement</label>
        <div className="array-input-group">
          {formData.performanceDetails.improvements.map((improvement, index) => (
            <div key={index} className="array-input-item">
              <input
                type="text"
                className="form-control"
                value={improvement}
                onChange={(e) => {
                  const newImprovements = [...formData.performanceDetails.improvements];
                  newImprovements[index] = e.target.value;
                  setFormData(prev => ({
                    ...prev,
                    performanceDetails: { ...prev.performanceDetails, improvements: newImprovements }
                  }));
                }}
                placeholder="Enter improvement area"
              />
              <button
                type="button"
                className="btn btn-outline-danger btn-sm"
                onClick={() => handleArrayRemove('performanceDetails.improvements', index)}
              >
                <X size={16} />
              </button>
            </div>
          ))}
          <div className="array-input-add">
            <input
              type="text"
              className="form-control"
              placeholder="Add improvement area"
              onKeyPress={(e) => {
                if (e.key === 'Enter') {
                  handleArrayAdd('performanceDetails.improvements', e.target.value);
                  e.target.value = '';
                }
              }}
            />
            <button
              type="button"
              className="btn btn-outline-primary"
              onClick={(e) => {
                const input = e.target.previousElementSibling;
                if (input && input.value) {
                  handleArrayAdd('performanceDetails.improvements', input.value);
                  input.value = '';
                }
              }}
            >
              <Plus size={16} />
            </button>
          </div>
        </div>
      </div>
      
      <div className="form-group">
        <label className="form-label">Key Achievements</label>
        <div className="array-input-group">
          {formData.performanceDetails.achievements.map((achievement, index) => (
            <div key={index} className="array-input-item">
              <input
                type="text"
                className="form-control"
                value={achievement}
                onChange={(e) => {
                  const newAchievements = [...formData.performanceDetails.achievements];
                  newAchievements[index] = e.target.value;
                  setFormData(prev => ({
                    ...prev,
                    performanceDetails: { ...prev.performanceDetails, achievements: newAchievements }
                  }));
                }}
                placeholder="Enter achievement"
              />
              <button
                type="button"
                className="btn btn-outline-danger btn-sm"
                onClick={() => handleArrayRemove('performanceDetails.achievements', index)}
              >
                <X size={16} />
              </button>
            </div>
          ))}
          <div className="array-input-add">
            <input
              type="text"
              className="form-control"
              placeholder="Add achievement"
              onKeyPress={(e) => {
                if (e.key === 'Enter') {
                  handleArrayAdd('performanceDetails.achievements', e.target.value);
                  e.target.value = '';
                }
              }}
            />
            <button
              type="button"
              className="btn btn-outline-primary"
              onClick={(e) => {
                const input = e.target.previousElementSibling;
                if (input && input.value) {
                  handleArrayAdd('performanceDetails.achievements', input.value);
                  input.value = '';
                }
              }}
            >
              <Plus size={16} />
            </button>
          </div>
        </div>
      </div>
      
      <div className="form-group">
        <h4 className="section-subtitle">Goals and Objectives</h4>
        <div className="form-grid-2">
          <div className="form-group">
            <label className="form-label">Previous Goals Achievement (%)</label>
            <input
              type="number"
              className="form-control"
              value={formData.goals.goalAchievement}
              onChange={(e) => handleNestedInputChange('goals', 'goalAchievement', e.target.value)}
              min="0"
              max="100"
            />
          </div>
          
          <div className="form-group">
            <label className="form-label">Goal Clarity Score</label>
            <input
              type="number"
              className="form-control"
              value={formData.goals.goalClarity}
              onChange={(e) => handleNestedInputChange('goals', 'goalClarity', e.target.value)}
              min="0"
              max="10"
            />
          </div>
        </div>
      </div>
    </div>
  );

  const renderFeedbackActions = () => (
    <div className="project-form-section">
      <div className="section-header">
        <h3 className="section-title">Feedback & Action Items</h3>
        <p className="section-description">Comprehensive feedback, recommendations, and improvement plans</p>
      </div>
      
      <div className="form-group">
        <label className="form-label">Manager Comments</label>
        <textarea
          className="form-control"
          rows="4"
          value={formData.feedback.managerComments}
          onChange={(e) => handleNestedInputChange('feedback', 'managerComments', e.target.value)}
          placeholder="Enter detailed manager feedback and observations"
        />
      </div>
      
      <div className="form-group">
        <label className="form-label">Subcontractor Comments</label>
        <textarea
          className="form-control"
          rows="4"
          value={formData.feedback.subcontractorComments}
          onChange={(e) => handleNestedInputChange('feedback', 'subcontractorComments', e.target.value)}
          placeholder="Enter subcontractor self-assessment and comments"
        />
      </div>
      
      <div className="form-group">
        <label className="form-label">Client Feedback</label>
        <textarea
          className="form-control"
          rows="3"
          value={formData.feedback.clientFeedback}
          onChange={(e) => handleNestedInputChange('feedback', 'clientFeedback', e.target.value)}
          placeholder="Enter client feedback and satisfaction comments"
        />
      </div>
      
      <div className="form-group">
        <label className="form-label">Recommendations</label>
        <textarea
          className="form-control"
          rows="4"
          value={formData.feedback.recommendations}
          onChange={(e) => handleNestedInputChange('feedback', 'recommendations', e.target.value)}
          placeholder="Enter recommendations for improvement and future performance"
        />
      </div>
      
      <div className="form-group">
        <h4 className="section-subtitle">Action Items</h4>
        <div className="array-input-group">
          {formData.actionItems.map((item, index) => (
            <div key={index} className="array-input-item">
              <input
                type="text"
                className="form-control"
                value={item}
                onChange={(e) => {
                  const newItems = [...formData.actionItems];
                  newItems[index] = e.target.value;
                  setFormData(prev => ({ ...prev, actionItems: newItems }));
                }}
                placeholder="Enter action item"
              />
              <button
                type="button"
                className="btn btn-outline-danger btn-sm"
                onClick={() => handleArrayRemove('actionItems', index)}
              >
                <X size={16} />
              </button>
            </div>
          ))}
          <div className="array-input-add">
            <input
              type="text"
              className="form-control"
              placeholder="Add action item"
              onKeyPress={(e) => {
                if (e.key === 'Enter') {
                  handleArrayAdd('actionItems', e.target.value);
                  e.target.value = '';
                }
              }}
            />
            <button
              type="button"
              className="btn btn-outline-primary"
              onClick={(e) => {
                const input = e.target.previousElementSibling;
                if (input && input.value) {
                  handleArrayAdd('actionItems', input.value);
                  input.value = '';
                }
              }}
            >
              <Plus size={16} />
            </button>
          </div>
        </div>
      </div>
      
      <div className="form-group">
        <h4 className="section-subtitle">Improvement Plan</h4>
        <div className="form-grid-2">
          <div className="form-group">
            <label className="form-label">Timeline</label>
            <input
              type="text"
              className="form-control"
              value={formData.improvementPlan.timeline}
              onChange={(e) => handleNestedInputChange('improvementPlan', 'timeline', e.target.value)}
              placeholder="e.g., 3 months, 6 months"
            />
          </div>
          
          <div className="form-group">
            <label className="form-label">Resources Required</label>
            <input
              type="text"
              className="form-control"
              value={formData.improvementPlan.resources.join(', ')}
              onChange={(e) => {
                const resources = e.target.value.split(',').map(r => r.trim()).filter(r => r);
                setFormData(prev => ({
                  ...prev,
                  improvementPlan: { ...prev.improvementPlan, resources }
                }));
              }}
              placeholder="Enter required resources"
            />
          </div>
        </div>
      </div>
      
      <div className="form-group">
        <h4 className="section-subtitle">Review Process</h4>
        <div className="form-grid-4">
          <div className="form-group">
            <label className="form-label">Reviewer</label>
            <select
              className="form-select"
              value={formData.reviewProcess.reviewer}
              onChange={(e) => handleNestedInputChange('reviewProcess', 'reviewer', e.target.value)}
            >
              <option value="">Select reviewer</option>
              <option value="john-doe">John Doe</option>
              <option value="jane-smith">Jane Smith</option>
              <option value="mike-johnson">Mike Johnson</option>
            </select>
          </div>
          
          <div className="form-group">
            <label className="form-label">Review Date</label>
            <input
              type="date"
              className="form-control"
              value={formData.reviewProcess.reviewDate}
              onChange={(e) => handleNestedInputChange('reviewProcess', 'reviewDate', e.target.value)}
            />
          </div>
          
          <div className="form-group">
            <label className="form-label">Next Review Date</label>
            <input
              type="date"
              className="form-control"
              value={formData.reviewProcess.nextReviewDate}
              onChange={(e) => handleNestedInputChange('reviewProcess', 'nextReviewDate', e.target.value)}
            />
          </div>
          
          <div className="form-group">
            <label className="form-label">Approval Status</label>
            <select
              className="form-select"
              value={formData.reviewProcess.approvalStatus}
              onChange={(e) => handleNestedInputChange('reviewProcess', 'approvalStatus', e.target.value)}
            >
              <option value="pending">Pending</option>
              <option value="approved">Approved</option>
              <option value="rejected">Rejected</option>
              <option value="under-review">Under Review</option>
            </select>
          </div>
        </div>
      </div>
    </div>
  );

  return (
    <>
      {/* Breadcrumb - Outside page-container to align with sidebar */}
      <PageBreadcrumb />
      {/* Page Header - Outside page-container to align with sidebar */}
      <PageHeader 
        title={viewMode ? "View Performance Review" : editMode ? "Edit Performance Review" : "Create Performance Review"}
        description="Comprehensive performance evaluation and feedback system"
      />
      <div className={`page-container ${viewMode ? 'view-mode' : ''}`} style={{ width: '100%', maxWidth: '100%' }}>
        {/* Main Content */}
        <div className="project-create-container" style={{ width: '100%', maxWidth: '100%' }}>
        <form onSubmit={handleSubmit} style={{ width: '100%' }} className={viewMode ? 'view-mode' : ''}>
          {/* Tab Navigation */}
          <div className="project-tabs">
            <div className="project-tabs-nav">
              {tabs.map((tab) => (
                <button
                  key={tab.id}
                  type="button"
                  className={`project-tab ${activeTab === tab.id ? 'active' : ''}`}
                  onClick={() => setActiveTab(tab.id)}
                >
                  <div className="project-tab-icon">
                    {tab.icon}
                  </div>
                  <div className="project-tab-content">
                    <div className="project-tab-label">{tab.label}</div>
                  </div>
                </button>
              ))}
            </div>
          </div>

          {/* Tab Content */}
          <div className="project-form-card">
            {renderTabContent()}
          </div>

          {/* Form Actions */}
          <div className="project-form-actions" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <div>
              {testMode && !editMode && !viewMode && (
                <button
                  type="button"
                  className="btn btn-outline"
                  onClick={fillTestData}
                  style={{ 
                    display: 'flex', 
                    alignItems: 'center', 
                    gap: '8px',
                    backgroundColor: '#ffffff',
                    borderColor: '#d1d5db'
                  }}
                >
                  <Database size={18} />
                  Fill Test Data
                </button>
              )}
            </div>
            <div style={{ display: 'flex', gap: '12px' }}>
              {viewMode ? (
                <>
                  <button
                    type="button"
                    className="btn btn-outline"
                    onClick={() => navigate('/subcontracting/performance')}
                  >
                    <ArrowLeft size={18} />
                    Back to List
                  </button>
                  <button
                    type="button"
                    className="btn btn-primary"
                    onClick={() => navigate(`/subcontracting/performance/edit/${reviewId}`)}
                    disabled={!reviewId || viewMode}
                  >
                    <Edit size={18} />
                    Edit Performance Review
                  </button>
                </>
              ) : (
                <>
                  <button
                    type="button"
                    className="btn btn-outline"
                    onClick={() => navigate('/subcontracting/performance')}
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="btn btn-primary"
                    disabled={isSubmitting || viewMode}
                  >
                    {isSubmitting ? (
                      <>
                        <div className="spinner-border spinner-border-sm me-2" role="status">
                          <span className="visually-hidden">Loading...</span>
                        </div>
                        {editMode ? 'Updating Review...' : 'Creating Review...'}
                      </>
                    ) : (
                      <>
                        <Save size={18} />
                        {editMode ? 'Update Performance Review' : 'Create Performance Review'}
                      </>
                    )}
                  </button>
                </>
              )}
            </div>
          </div>
        </form>
        </div>
      </div>
      <ToastContainer toasts={toasts} removeToast={removeToast} />
      <style>{`
        .view-mode input,
        .view-mode select,
        .view-mode textarea {
          pointer-events: none;
          opacity: 0.7;
          background-color: #f8f9fa !important;
        }
        .view-mode .array-input-add,
        .view-mode .btn-outline-danger {
          display: none !important;
        }
        .view-mode button[type="submit"] {
          display: none !important;
        }
        .view-mode .project-tab {
          cursor: pointer;
        }
      `}</style>
    </>
  );
};

export default CreatePerformanceReview;
